import { useCallback, useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { EntityLocationFields } from '@/components/ui/entity-location-fields'
import { EntityOrganisationCompanyFields } from '@/components/ui/entity-organisation-company-fields'
import { WebsiteContactField } from '@/components/ui/social-contact-field'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import { defaultEntityCountryCode } from '@/utils/entity-location-fields'
import {
  loadManagedOrganisationProfile,
  resolveEmployerOnboardingOrgSlug,
  updateManagedOrganisationProfile,
  type ManagedOrganisationOnboardingProfile,
} from '@/lib/employer-onboarding-organisation-api'
import {
  draftOrganisationDetails,
  websiteDomain,
  type OrganisationPrefillSocialField,
} from '@/lib/organisation-prefill-api'
import { foundedYearDraftValue, parseFoundedYearForSave } from '@/lib/organisation-company-meta'
import { prepareWebsiteForSave } from '@/lib/normalize-website-url'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'
import { isOrganisationPrefillEnabled, MARKDOWN_FIELD_MAX_LENGTH } from '@/constants'
import { markdownFieldLengthError } from '@/lib/rich-text-visible-char-count'

interface EmployerCompanyBioStepProps {
  step: OnboardingStep
  orgSlug?: string
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

const SOCIAL_LABELS: Record<OrganisationPrefillSocialField, string> = {
  linkedin_url: 'LinkedIn',
  x_url: 'X',
  instagram_url: 'Instagram',
  facebook_url: 'Facebook',
  youtube_url: 'YouTube',
  github_url: 'GitHub',
  tiktok_url: 'TikTok',
}

type DraftStatus =
  | { kind: 'idle' }
  | { kind: 'drafting'; domain: string }
  | { kind: 'drafted'; domain: string }
  | { kind: 'unreadable'; domain: string }

export function EmployerCompanyBioStep({
  orgSlug: orgSlugProp,
  onNext,
  onBack,
  isCompleting,
  canGoBack,
}: EmployerCompanyBioStepProps) {
  const prefillEnabled = isOrganisationPrefillEnabled()
  const defaultCountry = defaultEntityCountryCode(CLIENT_LOCALE)
  const [description, setDescription] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState(defaultCountry)
  const [website, setWebsite] = useState('')
  const [industry, setIndustry] = useState('')
  const [companySize, setCompanySize] = useState('')
  const [foundedYear, setFoundedYear] = useState('')
  const [suggestedSocials, setSuggestedSocials] = useState<
    Partial<Record<OrganisationPrefillSocialField, string>>
  >({})
  const [includeSocials, setIncludeSocials] = useState(true)
  const [draftStatus, setDraftStatus] = useState<DraftStatus>({ kind: 'idle' })
  const [orgSlug, setOrgSlug] = useState<string | null>(orgSlugProp ?? null)
  const [loading, setLoading] = useState(!orgSlugProp)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const savedProfileRef = useRef<ManagedOrganisationOnboardingProfile | null>(null)
  const draftAbortRef = useRef<AbortController | null>(null)

  /** Fill only the fields that are still empty; never overwrite what the user typed. */
  const applyDraft = useCallback(
    async (domain: string) => {
      draftAbortRef.current?.abort()
      const controller = new AbortController()
      draftAbortRef.current = controller
      setDraftStatus({ kind: 'drafting', domain })
      try {
        const draft = await draftOrganisationDetails(domain, controller.signal)
        if (controller.signal.aborted) return
        const keep = (current: string, next: string) => (current.trim() ? current : next)
        setDescription((cur) => keep(cur, draft.description))
        setIndustry((cur) => keep(cur, draft.industry))
        setCompanySize((cur) => keep(cur, draft.company_size))
        setFoundedYear((cur) => keep(cur, foundedYearDraftValue(draft.founded_year)))
        const saved = savedProfileRef.current
        setCity((cur) => keep(cur, draft.city))
        if (draft.country && !saved?.country) setCountry(draft.country)
        const socials: Partial<Record<OrganisationPrefillSocialField, string>> = {}
        for (const [field, url] of Object.entries(draft.socials)) {
          const key = field as OrganisationPrefillSocialField
          if (url && key in SOCIAL_LABELS && !saved?.[key]) socials[key] = url
        }
        setSuggestedSocials(socials)
        const found =
          draft.description || draft.industry || draft.city || Object.keys(socials).length
        setDraftStatus({ kind: found ? 'drafted' : 'unreadable', domain })
      } catch (err) {
        if (controller.signal.aborted) return
        console.error('Organisation draft failed:', err)
        setDraftStatus({ kind: 'unreadable', domain })
      }
    },
    [],
  )

  useEffect(() => () => draftAbortRef.current?.abort(), [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const slug = orgSlugProp ?? (await resolveEmployerOnboardingOrgSlug())
        if (!slug) {
          if (!cancelled) setError('Create your organisation first, then add a company bio.')
          return
        }
        if (!cancelled) setOrgSlug(slug)

        const profile = await loadManagedOrganisationProfile(slug)
        if (cancelled) return
        savedProfileRef.current = profile
        setDescription(profile.description)
        setCity(profile.city)
        setCountry(profile.country || defaultCountry)
        setWebsite(profile.website)
        setIndustry(profile.industry)
        setCompanySize(profile.company_size)
        setFoundedYear(foundedYearDraftValue(profile.founded_year))

        const domain = websiteDomain(profile.website)
        const hasGaps = !profile.description.trim() || !profile.industry.trim() || !profile.city
        if (prefillEnabled && domain && hasGaps) void applyDraft(domain)
      } catch (err) {
        console.error('Failed to load organisation profile:', err)
        if (!cancelled) setError('Failed to load organisation details')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [applyDraft, defaultCountry, orgSlugProp, prefillEnabled])

  const handleContinue = async () => {
    if (!orgSlug) {
      setError('Create your organisation first, then add a company bio.')
      return
    }

    setSaving(true)
    setError(null)

    const aboutLengthErr = markdownFieldLengthError(description, 'Company bio')
    const websiteResult = prepareWebsiteForSave(website)
    const foundedResult = parseFoundedYearForSave(foundedYear)
    const validationErr =
      aboutLengthErr ||
      (prefillEnabled ? websiteResult.error || foundedResult.error : null)
    if (validationErr) {
      setError(validationErr)
      setSaving(false)
      return
    }

    try {
      await updateManagedOrganisationProfile(orgSlug, {
        description: description.trim(),
        city: city.trim(),
        country: country.trim(),
        ...(prefillEnabled
          ? {
              website: websiteResult.website,
              industry: industry.trim(),
              company_size: companySize,
              founded_year: foundedResult.founded_year,
              ...(includeSocials ? suggestedSocials : {}),
            }
          : {}),
      })
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      console.error('Error saving company bio:', err)
      setError(err instanceof Error ? err.message : 'Failed to save company information')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading your organisation…</p>
  }

  const busy = saving || isCompleting
  const drafting = draftStatus.kind === 'drafting'
  const typedDomain = websiteDomain(website)
  const socialEntries = Object.entries(suggestedSocials) as [OrganisationPrefillSocialField, string][]

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {prefillEnabled ? (
          <div className="space-y-2">
            <WebsiteContactField
              id="company-website"
              value={website}
              onChange={setWebsite}
              disabled={busy}
            />
            {typedDomain && !drafting ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void applyDraft(typedDomain)}
              >
                <Sparkles aria-hidden />
                Suggest details from {typedDomain}
              </Button>
            ) : null}
          </div>
        ) : null}

        {draftStatus.kind !== 'idle' ? (
          <p className="text-sm text-muted-foreground" role="status">
            {draftStatus.kind === 'drafting'
              ? `Reading ${draftStatus.domain}…`
              : draftStatus.kind === 'drafted'
                ? `Suggested from ${draftStatus.domain}. Check the details before you continue.`
                : `We couldn't read ${draftStatus.domain}. Fill in the details below.`}
          </p>
        ) : null}

        <FormField label="Company bio" htmlFor="company-bio">
          <Textarea
            id="company-bio"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={busy}
            placeholder="What does your company do? Who do you hire for?"
            rows={5}
            maxLength={MARKDOWN_FIELD_MAX_LENGTH}
          />
        </FormField>

        {prefillEnabled ? (
          <>
            <FormField label="Industry" htmlFor="company-industry">
              <Input
                id="company-industry"
                value={industry}
                onChange={(event) => setIndustry(event.target.value)}
                disabled={busy}
                placeholder="e.g. Healthcare, Construction, Software"
              />
            </FormField>
            <EntityOrganisationCompanyFields
              idPrefix="company"
              value={{ company_size: companySize, founded_year: foundedYear }}
              onChange={(patch) => {
                if (patch.company_size !== undefined) setCompanySize(patch.company_size)
                if (patch.founded_year !== undefined) setFoundedYear(patch.founded_year)
              }}
              disabled={busy}
            />
          </>
        ) : null}

        <EntityLocationFields
          idPrefix="company"
          value={{ city, country }}
          onChange={(patch) => {
            if (patch.city !== undefined) setCity(patch.city)
            if (patch.country !== undefined) setCountry(patch.country)
          }}
          disabled={busy}
        />

        {socialEntries.length ? (
          <div className="flex items-start gap-3">
            <Checkbox
              id="company-include-socials"
              checked={includeSocials}
              onCheckedChange={(checked) => setIncludeSocials(checked === true)}
              disabled={busy}
              className="mt-0.5"
            />
            <div className="min-w-0 space-y-1">
              <Label htmlFor="company-include-socials">Add social links we found</Label>
              <ul className="space-y-0.5 text-sm text-muted-foreground">
                {socialEntries.map(([field, url]) => (
                  <li key={field} className="truncate">
                    {SOCIAL_LABELS[field]}: {url.replace(/^https?:\/\//, '')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleContinue}
        backDisabled={busy}
        continueDisabled={busy || !orgSlug}
        isBusy={saving}
        continueBusyLabel="Saving..."
      />
    </div>
  )
}
